import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Mail, Save, ShieldCheck, Send, Loader2, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Zap, BarChart2, AlertTriangle,
  Globe, Search, Shield, Info, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [
  { label: "Brevo", host: "smtp-relay.brevo.com", port: 587, tls: true },
  { label: "Gmail", host: "smtp.gmail.com", port: 587, tls: true },
  { label: "Outlook", host: "smtp-mail.outlook.com", port: 587, tls: true },
  { label: "Custom", host: "", port: 587, tls: true },
];

export default function Settings() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", company: "", from_email: "", from_name: "" });
  const [smtp, setSmtp] = useState({ host: "", port: 587, user: "", pass: "", tls: true });
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [usage, setUsage] = useState(null);
  const [healthDomain, setHealthDomain] = useState("");
  const [dkimSelector, setDkimSelector] = useState("default");
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthResult, setHealthResult] = useState(null);

  const loadUsage = async () => {
    try {
      const r = await api.get("/admin/usage");
      setUsage(r.data);
    } catch (_) {}
  };

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        company: user.company || "",
        from_email: user.from_email || "",
        from_name: user.from_name || "",
      });
    }
  }, [user]);

  useEffect(() => { loadUsage(); }, []);

  // Auto-extract domain from user's from_email
  useEffect(() => {
    if (user?.from_email && user.from_email.includes("@")) {
      setHealthDomain(user.from_email.split("@")[1]);
    }
  }, [user?.from_email]);

  const applyPreset = (preset) => {
    setSelectedPreset(preset.label);
    setSmtp((s) => ({ ...s, host: preset.host, port: preset.port, tls: preset.tls }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", {
        name: profile.name,
        company: profile.company,
        from_email: profile.from_email,
        from_name: profile.from_name,
      });
      toast.success("Profile saved");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const saveSmtp = async () => {
    if (!smtp.host || !smtp.user || !smtp.pass) {
      toast.error("Host, username, and password are required");
      return;
    }
    setSavingSmtp(true);
    try {
      await api.post("/settings/smtp", {
        smtp_host: smtp.host,
        smtp_port: smtp.port,
        smtp_user: smtp.user,
        smtp_pass: smtp.pass,
        smtp_use_tls: smtp.tls,
      });
      toast.success("Custom SMTP saved");
      await refresh();
      setSmtp((s) => ({ ...s, pass: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingSmtp(false); }
  };

  const testSmtp = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/settings/smtp/test");
      setTestResult("ok");
      toast.success(r.data.message || "Test email sent!");
    } catch (e) {
      setTestResult("error");
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const runDiagnostics = async () => {
    setHealthLoading(true);
    setHealthResult(null);
    try {
      const r = await api.get(`/settings/domain-health?domain=${healthDomain}&selector=${dkimSelector}`);
      setHealthResult(r.data);
      toast.success("Domain scan complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Domain health check failed");
    } finally {
      setHealthLoading(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-3xl mx-auto">
      <div className="label-mono mb-2">— Settings</div>
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-8">Profile & sending</h1>

      {/* ── Profile ── */}
      <form onSubmit={saveProfile} className="bg-white border border-[#EAEAE3] rounded-2xl p-6 space-y-5 mb-5 shadow-soft">
        <div className="font-display text-lg font-bold flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#002FA7]" /> Profile
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="label-mono">Name</Label><Input className="mt-2 rounded-lg" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} /></div>
          <div><Label className="label-mono">Email</Label><Input className="mt-2 rounded-lg bg-[#FAFAF7]" value={user?.email || ""} readOnly /></div>
          <div><Label className="label-mono">Company</Label><Input className="mt-2 rounded-lg" value={profile.company} onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))} /></div>
          <div><Label className="label-mono">Plan</Label><Input className="mt-2 rounded-lg uppercase font-mono bg-[#FAFAF7]" value={user?.plan || ""} readOnly /></div>
        </div>

        {/* Sender identity */}
        <div className="border-t border-[#EAEAE3] pt-5">
          <div className="font-display text-lg font-bold mb-1">Sender identity</div>
          <p className="text-sm text-[#5C5C66] mb-4">
            The name and email address that appears in your prospects' inboxes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="label-mono">From name</Label>
              <Input className="mt-2 rounded-lg" placeholder="Avery from Acme" value={profile.from_name} onChange={(e) => setProfile((p) => ({ ...p, from_name: e.target.value }))} />
            </div>
            <div>
              <Label className="label-mono">From email</Label>
              <Input type="email" className="mt-2 rounded-lg" placeholder="avery@yourcompany.com" value={profile.from_email} onChange={(e) => setProfile((p) => ({ ...p, from_email: e.target.value }))} />
            </div>
          </div>
        </div>

        <Button type="submit" disabled={saving} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save profile"}
        </Button>
      </form>

      {/* ── Platform usage monitor ── */}
      {usage && (
        <div className={`rounded-2xl p-5 mb-5 border shadow-soft ${
          usage.today.status === "critical" ? "bg-red-50 border-red-200" :
          usage.today.status === "warning"  ? "bg-amber-50 border-amber-200" :
          "bg-white border-[#EAEAE3]"
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-base font-bold flex items-center gap-2">
              <BarChart2 className={`h-4 w-4 ${
                usage.today.status === "critical" ? "text-red-600" :
                usage.today.status === "warning"  ? "text-amber-600" :
                "text-[#002FA7]"
              }`} />
              Platform sending — today
            </div>
            <span className={`font-mono text-xs font-semibold px-2 py-0.5 rounded-md ${
              usage.today.status === "critical" ? "bg-red-100 text-red-700" :
              usage.today.status === "warning"  ? "bg-amber-100 text-amber-700" :
              "bg-green-50 text-green-700"
            }`}>
              {usage.today.used_pct}% used
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-black/10 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full rounded-full transition-all ${
                usage.today.status === "critical" ? "bg-red-500" :
                usage.today.status === "warning"  ? "bg-amber-500" :
                "bg-[#002FA7]"
              }`}
              style={{ width: `${Math.min(usage.today.used_pct, 100)}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs text-[#5C5C66]">
            <span><strong className="text-[#0A0A0F]">{usage.today.sent}</strong> sent today · <strong className="text-[#0A0A0F]">{usage.month.sent}</strong> this month</span>
            <span>Limit: {usage.today.limit}/day</span>
          </div>

          {usage.today.status !== "ok" && (
            <div className={`mt-3 pt-3 border-t flex items-start gap-2 text-xs ${
              usage.today.status === "critical" ? "border-red-200 text-red-800" : "border-amber-200 text-amber-800"
            }`}>
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                {usage.today.status === "critical"
                  ? "Approaching daily limit — upgrade your Brevo plan now to avoid delivery failures."
                  : "75% of daily limit used — consider upgrading Brevo before end of day."}
                {" "}<a href="https://app.brevo.com/subscription/list" target="_blank" rel="noreferrer"
                  className="underline font-semibold">Upgrade plan →</a>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Email sending status ── */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-bold flex items-center gap-2">
            <Send className="h-4 w-4 text-[#002FA7]" /> Email sending
          </div>
          <span className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-widest bg-green-50 text-green-700 px-2.5 py-1 rounded-md border border-green-200">
            <ShieldCheck className="h-3 w-3" /> Ready
          </span>
        </div>

        <div className="flex items-start gap-4 bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-4">
          <div className="w-9 h-9 rounded-xl bg-[#002FA7] flex items-center justify-center shrink-0 mt-0.5">
            <Zap className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="font-medium text-sm mb-1">Powered by LeadsB2B's infrastructure</div>
            <div className="text-xs text-[#5C5C66] leading-relaxed">
              Your emails are sent through our verified sending infrastructure — no setup required.
              Just fill in your <strong>From name</strong> and <strong>From email</strong> above
              and you're ready to send. Replies go straight to your inbox.
            </div>
          </div>
        </div>

        {/* Advanced / custom SMTP toggle */}
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          className="mt-4 flex items-center gap-2 text-xs text-[#5C5C66] hover:text-[#0A0A0F] transition-colors"
        >
          {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Advanced: use my own SMTP server
        </button>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-[#EAEAE3] space-y-4">
            <p className="text-xs text-[#5C5C66]">
              Connect your own SMTP server to send from your exact domain and bypass shared sending limits.
              Leave blank to keep using LeadsB2B's built-in infrastructure.
            </p>

            {/* Presets */}
            <div className="flex gap-2 flex-wrap">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    selectedPreset === p.label
                      ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]"
                      : "border-[#EAEAE3] text-[#5C5C66] hover:border-[#002FA7]/30"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="label-mono">SMTP host</Label>
                <Input className="mt-2 rounded-lg font-mono text-sm" value={smtp.host} onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))} placeholder="smtp-relay.brevo.com" />
              </div>
              <div>
                <Label className="label-mono">Port</Label>
                <Input type="number" className="mt-2 rounded-lg font-mono text-sm" value={smtp.port} onChange={(e) => setSmtp((s) => ({ ...s, port: parseInt(e.target.value) || 587 }))} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="label-mono">SMTP username</Label>
                <Input className="mt-2 rounded-lg font-mono text-sm" value={smtp.user} onChange={(e) => setSmtp((s) => ({ ...s, user: e.target.value }))} placeholder="you@company.com" />
              </div>
              <div>
                <Label className="label-mono">SMTP password / key</Label>
                <Input type="password" className="mt-2 rounded-lg font-mono text-sm" value={smtp.pass} onChange={(e) => setSmtp((s) => ({ ...s, pass: e.target.value }))} placeholder="Leave blank to keep current" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch id="tls" checked={smtp.tls} onCheckedChange={(v) => setSmtp((s) => ({ ...s, tls: v }))} />
              <Label htmlFor="tls" className="text-sm cursor-pointer">Use STARTTLS (recommended)</Label>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={saveSmtp} disabled={savingSmtp} size="sm" className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
                {savingSmtp ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : <><Save className="h-3.5 w-3.5 mr-1.5" />Save SMTP</>}
              </Button>
              {user?.has_smtp && (
                <Button onClick={testSmtp} disabled={testing} size="sm" variant="outline" className="rounded-lg border-[#EAEAE3]">
                  {testing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</> : <><Send className="h-3.5 w-3.5 mr-1.5" />Test</>}
                </Button>
              )}
              {testResult === "ok" && <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Passed</span>}
              {testResult === "error" && <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> Failed</span>}
            </div>
          </div>
        )}
      </div>

      {/* ── Domain Deliverability Health ── */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
        <div className="font-display text-lg font-bold flex items-center gap-2 mb-1">
          <Shield className="h-4 w-4 text-[#002FA7]" /> Domain deliverability health
        </div>
        <p className="text-sm text-[#5C5C66] mb-5">
          Check your sending domain's SPF, DKIM, DMARC, and MX records to maximize inbox placement.
        </p>

        <div className="flex flex-wrap gap-3 items-end mb-5">
          <div className="flex-1 min-w-[200px]">
            <Label className="label-mono">Domain</Label>
            <Input className="mt-2 rounded-lg font-mono text-sm" placeholder="yourcompany.com" value={healthDomain} onChange={(e) => setHealthDomain(e.target.value)} />
          </div>
          <div className="w-40">
            <Label className="label-mono">DKIM selector</Label>
            <Input className="mt-2 rounded-lg font-mono text-sm" placeholder="default" value={dkimSelector} onChange={(e) => setDkimSelector(e.target.value)} />
          </div>
          <Button onClick={runDiagnostics} disabled={healthLoading || !healthDomain} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
            {healthLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning...</> : <><Search className="h-4 w-4 mr-2" /> Run diagnostics</>}
          </Button>
        </div>

        {healthResult && (
          <div className="space-y-5 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
            {/* Score Header */}
            <div className={`rounded-xl p-5 border ${healthResult.overall_score >= 90 ? 'bg-green-50 border-green-200' : healthResult.overall_score >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {healthResult.overall_score}<span className="text-base font-medium text-[#5C5C66]">/100</span>
                  </div>
                  <div className="text-sm text-[#5C5C66] mt-0.5">
                    Deliverability score for <strong className="font-mono">{healthResult.domain}</strong>
                  </div>
                </div>
                <span className={`font-mono text-xs font-bold px-3 py-1.5 rounded-lg ${
                  healthResult.rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                  healthResult.rating === 'Good' ? 'bg-emerald-100 text-emerald-800' :
                  healthResult.rating === 'Fair' ? 'bg-amber-100 text-amber-800' :
                  'bg-red-100 text-red-800'
                }`}>{healthResult.rating}</span>
              </div>
              {/* Score bar */}
              <div className="h-2.5 bg-black/10 rounded-full overflow-hidden mt-4">
                <div className={`h-full rounded-full transition-all duration-700 ${healthResult.overall_score >= 90 ? 'bg-green-500' : healthResult.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${healthResult.overall_score}%` }} />
              </div>
            </div>

            {/* Individual Check Cards */}
            {healthResult.checks.map((check) => (
              <div key={check.name} className="border border-[#EAEAE3] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {check.status === 'configured' ? (
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /></div>
                    ) : check.status === 'warning' ? (
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /></div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center"><XCircle className="h-3.5 w-3.5 text-red-600" /></div>
                    )}
                    <span className="font-display font-bold">{check.name}</span>
                  </div>
                  <span className="font-mono text-xs text-[#5C5C66]">{check.score}/{check.max_score} pts</span>
                </div>
                <p className="text-sm text-[#5C5C66] ml-8 mb-2">{check.message}</p>
                {check.records.length > 0 && (
                  <div className="ml-8 mb-2">
                    <div className="label-mono text-[10px] mb-1">Active records</div>
                    <div className="bg-[#0A0A0F] text-green-400 rounded-lg p-3 font-mono text-xs overflow-x-auto max-h-24">
                      {check.records.map((r, i) => <div key={i}>{r}</div>)}
                    </div>
                  </div>
                )}
                {check.fix_instructions && (
                  <div className="ml-8 mt-2 p-3 bg-[#FAFAF7] border border-[#EAEAE3] rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-3.5 w-3.5 text-[#002FA7] mt-0.5 shrink-0" />
                      <div className="text-xs text-[#5C5C66] leading-relaxed">
                        <strong className="text-[#0A0A0F]">How to fix:</strong> {check.fix_instructions}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
